"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, CircleCheck, PawPrint, User, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
export default function AgendaTuVisita() {
  return <section data-section-id="222699" id="start-your-protection" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-8 items-start">
          
          {}
          <div className="flex flex-col space-y-8">
            <div className="space-y-4">
              <h2 className="font-default text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-foreground uppercase">
                Start Your Protection
              </h2>
              <p className="text-xl text-foreground max-w-[600px] font-serif">
                Secure your home today with Peace River Pest Control. Our licensed experts are ready to eliminate pests, termites, and mosquitoes from your property in Punta Gorda and beyond.
              </p>
            </div>

            

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {["FDACS Licensed & Insured", "Local Punta Gorda Experts", "Free Comprehensive Inspection", "Eco-Friendly Solutions"].map((benefit, i) => <div key={i} data-index={i} className="flex items-center gap-3 bg-card p-4 rounded-none border-2 border-foreground shadow-none">
                  <CircleCheck className="size-5 text-foreground shrink-0" />
                  <span className="text-sm font-bold text-card-foreground uppercase font-default">{benefit}</span>
                </div>)}
            </div>
          </div>

          {}
          <div className="bg-card rounded-none p-6 md:p-8 shadow-none border-4 border-foreground">
            <FormProvider formKey="protection_form_pest_control" formId="protection_form_pest_control" sectionName="Start Your Protection" defaultValues={{
            name: "",
            email: "",
            phone: "",
            pet_type: "",
            pet_name: "",
            visit_date: undefined,
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            pet_type: {
              required: true
            },
            pet_name: {
              required: true
            },
            visit_date: {
              required: true
            },
            message: {}
          }} submitText="Schedule Inspection" successMessage="Thank you! Your inspection request has been received. A Peace River specialist will contact you shortly." recipientEmail="steve@peaceriverpest.com">
              <MultiStepAppointmentForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}
function MultiStepAppointmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 bg-foreground/10 rounded-full flex items-center justify-center">
          <CircleCheck className="size-10 text-foreground" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-2xl font-bold text-foreground uppercase">Request Sent!</h3>
          <p className="text-foreground font-serif text-lg max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="mt-4 rounded-full border-2 border-foreground font-default uppercase font-bold">
          New Request
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {}
      <div className="space-y-4">
        <div className="flex justify-between items-end">
          <div className="space-y-1">
            <h3 className="font-default text-xl font-bold text-foreground flex items-center gap-2 uppercase">
              {step === 1 && <><PawPrint className="size-5 text-foreground" /> Service Type</>}
              {step === 2 && <><User className="size-5 text-foreground" /> Contact Info</>}
              {step === 3 && <><CalendarDays className="size-5 text-foreground" /> Schedule</>}
            </h3>
            <p className="text-sm text-foreground font-default font-medium">
              Step {step} of {totalSteps}
            </p>
          </div>
          <span className="text-sm font-bold text-foreground font-default">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-3 bg-foreground/20" />
      </div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        
        {}
        <div className={step === 1 ? "space-y-6 block animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
          <FormField control={control} name="pet_type" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Pest Problem</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-12 text-base rounded-none border-2 border-foreground bg-white">
                      <SelectValue placeholder="Select a pest type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-none border-2 border-foreground">
                    {["General Pests", "Termites", "Mosquitoes", "Spiders", "Rodents", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i} className="font-default">
                        {option}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="pet_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Property Address</FormLabel>
                <FormControl>
                  <input {...field} type="text" placeholder="123 Peace River Way, Punta Gorda" className="flex h-12 w-full rounded-none border-2 border-foreground bg-white px-3 py-2 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-foreground disabled:cursor-not-allowed disabled:opacity-50 font-default" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={step === 2 ? "space-y-6 block animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Full Name</FormLabel>
                <FormControl>
                  <input {...field} type="text" placeholder="John Smith" className="flex h-12 w-full rounded-none border-2 border-foreground bg-white px-3 py-2 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-foreground disabled:cursor-not-allowed disabled:opacity-50 font-default" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Email Address</FormLabel>
                <FormControl>
                  <input {...field} type="email" placeholder="john@example.com" className="flex h-12 w-full rounded-none border-2 border-foreground bg-white px-3 py-2 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-foreground disabled:cursor-not-allowed disabled:opacity-50 font-default" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Phone Number</FormLabel>
                <FormControl>
                  <input {...field} type="tel" placeholder="(941) 555-0123" className="flex h-12 w-full rounded-none border-2 border-foreground bg-white px-3 py-2 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-foreground disabled:cursor-not-allowed disabled:opacity-50 font-default" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={step === 3 ? "space-y-6 block animate-in slide-in-from-right-4 fade-in duration-300" : "hidden"}>
          <FormField control={control} name="visit_date" render={({
          field
        }) => <FormItem className="flex flex-col">
                <FormLabel className="font-default text-base font-bold uppercase">Preferred Inspection Date</FormLabel>
                <div className="rounded-none border-2 border-foreground bg-white p-3 shadow-none overflow-hidden flex justify-center">
                  <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="pointer-events-auto font-default" /></SafeHydrate>
                </div>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-base font-bold uppercase">Specific Concerns</FormLabel>
                <FormControl>
                  <textarea {...field} placeholder="Tell us about your pest issues..." className="flex min-h-[120px] w-full rounded-none border-2 border-foreground bg-white px-3 py-2 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-foreground disabled:cursor-not-allowed disabled:opacity-50 resize-y font-default" />
                </FormControl>
                <FormDescription className="font-default italic">Optional</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        {submitError && <div className="p-4 bg-destructive/10 border-2 border-destructive rounded-none flex items-start gap-3">
            <div className="text-destructive font-bold text-sm font-default uppercase">
              Please ensure all required fields in previous steps are completed.
            </div>
          </div>}

        {}
        <div className="flex items-center justify-between pt-6 border-t-2 border-foreground mt-8">
          <Button type="button" variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || isSubmitting} className="w-28 rounded-full border-2 border-foreground font-default font-bold uppercase">
            <ChevronLeft className="mr-2 size-4" /> Back
          </Button>

          {step < totalSteps ? <Button type="button" onClick={() => setStep(Math.min(totalSteps, step + 1))} className="w-28 rounded-full bg-foreground text-background hover:bg-foreground/90 font-default font-bold uppercase">
              Next <ChevronRight className="ml-2 size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="w-auto min-w-[180px] rounded-full bg-foreground text-background hover:bg-foreground/90 font-default font-bold uppercase">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Sending...
                </> : <>Schedule Now <CircleCheck className="ml-2 size-4" /></>}
            </Button>}
        </div>
      </Form>
    </div>;
}